// client/customer/src/pages/Reserve/ReserveSelectionSummary.tsx
import React from 'react';
import {
  CalendarDays,
  CheckCircle2,
  ChevronRight,
  CircleDashed,
  Clock3,
  Sparkles,
  Store,
  UserRound,
} from 'lucide-react';
import { useReserveContext } from '../../contexts/useReserveContext';
import { useI18n } from '../../i18n';

type Props = {
  customerName?: string | null;
  storeName?: string | null;
  date?: string | null;        // 'YYYY-MM-DD'
  startTime?: string | null;   // 'HH:mm'
  endTime?: string | null;     // 'HH:mm' (あれば)
  staffName?: string | null;

  variant?: 'dark' | 'light';  // 既存デザインに合わせる
  onSelectStore?: () => void;
  onSelectStaff?: () => void;
  onSelectDate?: () => void;
  onSelectTime?: () => void;
  onSelectServices?: () => void;
};

type ItemTone = 'emerald' | 'cyan';

// 'YYYY-MM-DD' -> 'DD/MM/YYYY'
const toDDMMYYYY = (s?: string | null) => {
  if (!s) return '-';
  const [y, m, d] = s.split('T')[0].split('-');
  if (!y || !m || !d) return '-';
  return `${d}/${m}/${y}`;
};

const ReserveSelectionSummary: React.FC<Props> = ({
  customerName,
  storeName,
  date,
  startTime,
  endTime,
  staffName,
  variant = 'dark',
  onSelectStore,
  onSelectStaff,
  onSelectDate,
  onSelectTime,
  onSelectServices,
}) => {
  const { t } = useI18n();
  const { selectedServices } = useReserveContext();
  const isDark = variant === 'dark';
  const cardClass = isDark
    ? 'border-slate-800 bg-slate-900 text-slate-100'
    : 'border-slate-200 bg-white text-slate-900';
  const itemClass = isDark
    ? 'rounded-2xl border border-white/10 bg-white/5 px-3 py-3'
    : 'rounded-2xl border border-slate-200 bg-slate-50 px-3 py-3';
  const interactiveItemClass = isDark
    ? 'transition hover:bg-white/10 active:scale-[0.99]'
    : 'transition hover:bg-white active:scale-[0.99]';
  const labelClass = isDark
    ? 'text-[11px] font-medium uppercase tracking-wide text-slate-300'
    : 'text-[11px] font-medium uppercase tracking-wide text-slate-500';
  const valueClass = isDark
    ? 'mt-1 text-sm font-semibold text-white'
    : 'mt-1 text-sm font-semibold text-slate-900';
  const chipClass = isDark
    ? 'rounded-full border border-white/10 bg-white/8 px-2.5 py-1 text-xs font-medium text-slate-200'
    : 'rounded-full border border-slate-200 bg-white px-2.5 py-1 text-xs font-medium text-slate-700';
  const dateTimeText = `${date ? toDDMMYYYY(date) : t('summary.notSelected')} ${startTime || ''}${endTime ? ` - ${endTime}` : ''}`.trim();
  const iconAccentStore = isDark ? 'text-emerald-300' : 'text-emerald-700';
  const iconAccentTime = isDark ? 'text-cyan-300' : 'text-cyan-700';
  const selectedServiceLabels = selectedServices.map((service) =>
    service.categoryName ? `${service.categoryName} • ${service.serviceName}` : service.serviceName
  );
  const ServicesComp = onSelectServices ? 'button' : 'div';

  const renderItem = ({
    label,
    value,
    icon,
    hasValue,
    tone,
    onSelect,
  }: {
    label: string;
    value: string;
    icon: React.ReactNode;
    hasValue: boolean;
    tone: ItemTone;
    onSelect?: () => void;
  }) => {
    const Comp = onSelect ? 'button' : 'div';
    const selectedClass = isDark
      ? tone === 'emerald'
        ? 'border-emerald-300/60 bg-gradient-to-br from-emerald-500/25 to-emerald-300/10 shadow-[0_12px_30px_-18px_rgba(16,185,129,0.9)]'
        : 'border-cyan-300/60 bg-gradient-to-br from-cyan-500/25 to-cyan-300/10 shadow-[0_12px_30px_-18px_rgba(6,182,212,0.9)]'
      : tone === 'emerald'
      ? 'border-emerald-400 bg-gradient-to-br from-emerald-100 via-emerald-50 to-white shadow-[0_14px_28px_-20px_rgba(16,185,129,0.85)]'
      : 'border-cyan-400 bg-gradient-to-br from-cyan-100 via-cyan-50 to-white shadow-[0_14px_28px_-20px_rgba(6,182,212,0.85)]';
    const unselectedClass = isDark
      ? 'border-dashed border-white/10 bg-slate-900/70 opacity-70'
      : 'border-dashed border-slate-300 bg-slate-50 opacity-80';
    const itemToneClass = hasValue ? selectedClass : unselectedClass;
    const itemLabelClass = hasValue
      ? labelClass
      : isDark
      ? 'text-[11px] font-medium uppercase tracking-wide text-slate-400'
      : 'text-[11px] font-medium uppercase tracking-wide text-slate-400';
    const itemValueClass = hasValue
      ? valueClass
      : isDark
      ? 'mt-1 text-sm font-semibold text-slate-500'
      : 'mt-1 text-sm font-semibold text-slate-400';
    const iconWrapClass = hasValue
      ? isDark
        ? tone === 'emerald'
          ? 'rounded-full bg-emerald-300/15 p-2 text-emerald-200'
          : 'rounded-full bg-cyan-300/15 p-2 text-cyan-200'
        : tone === 'emerald'
        ? 'rounded-full bg-emerald-600 p-2 text-white shadow-sm'
        : 'rounded-full bg-cyan-600 p-2 text-white shadow-sm'
      : isDark
      ? 'rounded-full bg-white/5 p-2 text-slate-500'
      : 'rounded-full bg-slate-200 p-2 text-slate-400';
    const statusPillClass = hasValue
      ? isDark
        ? tone === 'emerald'
          ? 'border border-emerald-300/30 bg-emerald-300/15 text-emerald-200'
          : 'border border-cyan-300/30 bg-cyan-300/15 text-cyan-200'
        : tone === 'emerald'
        ? 'border border-emerald-300 bg-emerald-600 text-white'
        : 'border border-cyan-300 bg-cyan-600 text-white'
      : isDark
      ? 'border border-slate-700 bg-slate-800 text-slate-400'
      : 'border border-slate-300 bg-white text-slate-500';

    return (
      <Comp
        {...(onSelect ? { type: 'button', onClick: onSelect } : {})}
        className={[
          itemClass,
          itemToneClass,
          onSelect ? interactiveItemClass : '',
          onSelect ? 'text-left' : '',
        ].join(' ')}
      >
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <div className={iconWrapClass}>{icon}</div>
            <div className={itemLabelClass}>{label}</div>
          </div>
          <div className="flex items-center gap-2">
            <div
              className={[
                'inline-flex items-center gap-1 rounded-full px-2 py-1 text-[10px] font-semibold uppercase tracking-[0.18em]',
                statusPillClass,
              ].join(' ')}
            >
              {hasValue ? <CheckCircle2 className="h-3.5 w-3.5" /> : <CircleDashed className="h-3.5 w-3.5" />}
              {hasValue ? t('summary.selected') : t('summary.pending')}
            </div>
            {onSelect ? (
              <ChevronRight className={`h-4 w-4 ${isDark ? 'text-slate-400' : 'text-slate-300'}`} />
            ) : null}
          </div>
        </div>
        <div className={itemValueClass}>{value}</div>
      </Comp>
    );
  };

  return (
    <div className={['rounded-[28px] border p-3 shadow-sm', cardClass].join(' ')}>
      <div className="grid grid-cols-2 gap-3">
        {renderItem({
          label: t('summary.store'),
          value: storeName || t('summary.notSelected'),
          icon: <Store className={`h-4 w-4 ${iconAccentStore}`} />,
          hasValue: Boolean(storeName),
          tone: 'emerald',
          onSelect: onSelectStore,
        })}
        {renderItem({
          label: t('summary.staff'),
          value: staffName || t('summary.notSelected'),
          icon: <UserRound className={`h-4 w-4 ${iconAccentStore}`} />,
          hasValue: Boolean(staffName),
          tone: 'emerald',
          onSelect: onSelectStaff,
        })}
        {renderItem({
          label: t('summary.date'),
          value: date ? toDDMMYYYY(date) : t('summary.notSelected'),
          icon: <CalendarDays className={`h-4 w-4 ${iconAccentTime}`} />,
          hasValue: Boolean(date),
          tone: 'cyan',
          onSelect: onSelectDate,
        })}
        {renderItem({
          label: t('summary.time'),
          value: dateTimeText.replace(toDDMMYYYY(date), '').trim() || t('summary.notSelected'),
          icon: <Clock3 className={`h-4 w-4 ${iconAccentTime}`} />,
          hasValue: Boolean(startTime),
          tone: 'cyan',
          onSelect: onSelectTime,
        })}
      </div>

      <ServicesComp
        {...(onSelectServices ? { type: 'button', onClick: onSelectServices } : {})}
        className={[
          'mt-3 rounded-2xl border px-3 py-3',
          isDark ? 'border-white/10 bg-white/5' : 'border-slate-200 bg-slate-50',
          onSelectServices ? interactiveItemClass : '',
          onSelectServices ? 'text-left' : '',
        ].join(' ')}
      >
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <div
              className={
                selectedServiceLabels.length > 0
                  ? isDark
                    ? 'rounded-full bg-amber-300/15 p-2 text-amber-200'
                    : 'rounded-full bg-amber-500 p-2 text-white shadow-sm'
                  : isDark
                  ? 'rounded-full bg-white/5 p-2 text-slate-500'
                  : 'rounded-full bg-slate-200 p-2 text-slate-400'
              }
            >
              <Sparkles className="h-4 w-4" />
            </div>
            <div className={selectedServiceLabels.length > 0 ? labelClass : 'text-[11px] font-medium uppercase tracking-wide text-slate-400'}>
              {t('summary.services')}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <div
              className={[
                'inline-flex items-center gap-1 rounded-full px-2 py-1 text-[10px] font-semibold uppercase tracking-[0.18em]',
                selectedServiceLabels.length > 0
                  ? isDark
                    ? 'border border-amber-300/30 bg-amber-300/15 text-amber-200'
                    : 'border border-amber-300 bg-amber-500 text-white'
                  : isDark
                  ? 'border border-slate-700 bg-slate-800 text-slate-400'
                  : 'border border-slate-300 bg-white text-slate-500',
              ].join(' ')}
            >
              {selectedServiceLabels.length > 0 ? (
                <CheckCircle2 className="h-3.5 w-3.5" />
              ) : (
                <CircleDashed className="h-3.5 w-3.5" />
              )}
              {selectedServiceLabels.length > 0
                ? t('summary.selectedCount', { count: selectedServiceLabels.length })
                : t('summary.pending')}
            </div>
            {onSelectServices ? (
              <ChevronRight className={`h-4 w-4 ${isDark ? 'text-slate-400' : 'text-slate-300'}`} />
            ) : null}
          </div>
        </div>

        {selectedServiceLabels.length > 0 ? (
          <div className="mt-3 flex flex-wrap gap-2">
            {selectedServiceLabels.map((label, index) => (
              <div key={`${label}-${index}`} className={chipClass}>
                {label}
              </div>
            ))}
          </div>
        ) : (
          <div
            className={
              isDark
                ? 'mt-1 text-sm font-semibold text-slate-500'
                : 'mt-1 text-sm font-semibold text-slate-400'
            }
          >
            {t('summary.notSelected')}
          </div>
        )}
      </ServicesComp>

      {customerName ? (
        <div className={['mt-3 rounded-2xl px-3 py-2 text-xs', isDark ? 'bg-white/5 text-slate-300' : 'bg-slate-50 text-slate-500'].join(' ')}>
          {t('summary.bookingFor', { name: customerName })}
        </div>
      ) : null}
    </div>
  );
};

export default ReserveSelectionSummary;
